<script setup lang="ts">
import { usePriceFormatter } from "~/utils/priceFormatter";

const { productPageUrl, getAssetUrl } = useHelpers();

// -----------------------
// props
// -----------------------
interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  products?: any[];
}

const props = defineProps<{
  messages: ChatMessage[];
  isLoading: boolean;
}>();

// -----------------------
// auto-scroll functionality
// -----------------------
const messagesContainer = ref<HTMLElement>();

// Watch for new messages and scroll to bottom
watch(
  () => props.messages.length,
  () => {
    nextTick(() => {
      scrollToBottom();
    });
  },
);

// Watch for loading state changes (when response arrives)
watch(
  () => props.isLoading,
  (newLoading: boolean, oldLoading: boolean) => {
    if (oldLoading && !newLoading) {
      // Loading finished, scroll to show the new response
      nextTick(() => {
        scrollToBottom();
      });
    }
  },
);

const scrollToBottom = () => {
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
};

// -----------------------
// helper functions
// -----------------------
const formatTime = (timestamp: Date) => {
  return new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(timestamp);
};
</script>

<template>
  <div ref="messagesContainer" class="chat-messages space-y-4 mainContent">
    <!-- Welcome message -->
    <div v-if="messages.length === 0" class="text-center text-gray-500 py-8">
      <div class="text-4xl mb-4">🤖</div>
      <p class="text-lg font-medium mb-2">{{ $t("chat.product_assistant") }}</p>
      <p class="text-sm">{{ $t("chat.ask_about_products") }}</p>
    </div>

    <!-- Messages -->
    <div
      v-for="message in messages"
      :key="message.id"
      class="flex"
      :class="message.role === 'user' ? 'justify-end' : 'justify-start'"
    >
      <div
        class="max-w-[80%] rounded-lg px-4 py-3"
        :class="
          message.role === 'user'
            ? 'bg-brand-highlight text-white'
            : 'bg-gray-100 text-gray-800'
        "
      >
        <!-- Message content -->
        <div class="whitespace-pre-wrap">{{ message.content }}</div>

        <!-- Timestamp -->
        <div
          class="text-xs mt-2 opacity-70"
          :class="message.role === 'user' ? 'text-right' : 'text-left'"
        >
          {{ formatTime(message.timestamp) }}
        </div>

        <!-- Product recommendations -->
        <div
          v-if="
            message.role === 'assistant' &&
            message.products &&
            message.products.length > 0
          "
          class="mt-4"
        >
          <div class="space-y-2">
            <div
              v-for="product in message.products"
              :key="product.id"
              class="bg-white rounded border p-3 hover:shadow-md transition-shadow cursor-pointer"
              @click="$router.push(productPageUrl(product))"
            >
              <div class="flex items-start space-x-3">
                <!-- Product image placeholder -->
                <div
                  class="w-[100px] h-[100px] flex items-center justify-center"
                >
                  <NuxtImg
                    v-if="product.images[0]"
                    width="200"
                    :alt="product.name"
                    class="object-center object-contain h-full aspect-square"
                    :src="getAssetUrl(product.images[0])"
                    format="webp"
                    loading="lazy"
                  />
                </div>

                <!-- Product info -->
                <div class="flex-1 min-w-0">
                  <div class="font-medium text-sm text-gray-900 truncate">
                    {{ product.name }}
                  </div>
                  <div class="text-xs text-gray-500">
                    {{ product.masterproduct_gyarto_property }}
                  </div>
                  <!-- eslint-disable vue/no-v-html -->
                  <span v-html="usePriceFormatter(product, 'masterProduct')" />
                  <!-- eslint-enable -->
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading indicator -->
    <div v-if="isLoading" class="flex justify-start">
      <div class="bg-gray-100 text-gray-800 rounded-lg px-4 py-3">
        <div class="flex items-center space-x-2">
          <div
            class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-highlight"
          ></div>
          <span class="text-sm">{{ $t("chat.thinking") }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.chat-messages {
  scroll-behavior: smooth;
  max-height: 60vh;
  overflow-y: auto;
}
</style>
